import { service } from '@maia/cart';
import { service as CmsService } from '@maia/cms';
import { mapper } from '@maia/icons';
import { BillingUnitDto, ProductsDto } from '@maia/shared';
import {
  BigPricingPill,
  cardStyles,
  CompositionCard,
  FeaturedIcon,
  RadioList,
  Switch,
  Typography,
} from '@maia/ui';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { getCompositionQuestionsOptions } from '../../../../../../../../features/add-insurance/get-composition-questions/queryOptions';
import { getCompositionsOptions } from '../../../../../../../../features/add-insurance/get-compositions/queryOptions';
import { Form } from '../../../../../../../../features/form/base-form/base-form';
import { calculateDefaultValues } from '../../../../../../../../features/form/utils/calculate-default-values';
import { mapToAnswer } from '../../../../../../../../features/form/utils/map-to-answers';
import { queryClient } from '../../../../../../../../main';
import { getCustomerOptions } from './../../../../../../../../features/customer/customer/queryOptions';

export const Route = createFileRoute(
  '/_authenticated/add-insurance/$customer/_layout-cart/cart/$cart/product/$product/_layout-cart/compositions',
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { customer: customerGuid, cart, product },
  }) => {
    const compositions = await queryClient.ensureQueryData(
      getCompositionsOptions({
        cartGuid: cart,
        productGuid: product,
        customerGuid: customerGuid,
      }),
    );

    const products = await queryClient.ensureQueryData(
      getCompositionQuestionsOptions({
        cartGuid: cart,
        productGuid: product,
        customerGuid: customerGuid,
      }),
    );

    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(customerGuid),
    );

    return { compositions, products, customer };
  },
});

function RouteComponent() {
  const {
    compositions: initialCompositionsData,
    products,
    customer,
  } = Route.useLoaderData();
  const { cart, customer: customerGuid, product } = Route.useParams();

  const { data: compositions, refetch: refetchCompositions } = useQuery({
    ...getCompositionsOptions({
      cartGuid: cart,
      productGuid: product,
      customerGuid: customerGuid,
    }),
    initialData: initialCompositionsData,
  });

  const results = useQueries({
    queries: compositions.map((c) => ({
      queryKey: ['compositions', c.guid],
      queryFn: async () => {
        const cmsData = await CmsService.getProductDetails({
          productGuid: c.guid,
        });
        return { ...cmsData, guid: c.guid };
      },
    })),
  });

  const {
    data: compositionQuestions,
    refetch,
    isFetching,
  } = useQuery({
    ...getCompositionQuestionsOptions({
      cartGuid: cart,
      productGuid: product,
      customerGuid: customerGuid,
    }),
    initialData: products,
  });

  const compositionSelectionForm = useForm({
    defaultValues: {
      compositionGuid: '',
    },
    onSubmit: ({ value }) => selectComposition(value.compositionGuid),
  });

  const { mutate: selectComposition, isPending: selectCompositionIsPending } =
    useMutation({
      mutationFn: (
        compositionGuid: Parameters<
          typeof service.putCompositions
        >[number]['compositionGuid'],
      ) =>
        service.putCompositions({
          cartGuid: cart,
          productGuid: product,
          customerGuid: customerGuid,
          compositionGuid,
        }),
      onSuccess: async () => {
        await refetch();
        return await queryClient.invalidateQueries({
          queryKey: ['products'],
          exact: false,
        });
      },
    });

  const { mutate: putAddonQuestion, isPending: putAddonIsPending } =
    useMutation({
      mutationFn: (
        questions: Parameters<typeof service.putQuestions>[number]['questions'],
      ) =>
        service.putQuestions({
          cartGuid: cart,
          productGuid: product,
          customerGuid: customerGuid,
          questions,
        }),
      onSuccess: () => {
        refetch();
        refetchCompositions();
        return queryClient.invalidateQueries({
          queryKey: ['products'],
          exact: false,
        });
      },
    });

  const activeComposition = compositionQuestions.composition;
  const isRevalidating =
    isFetching || selectCompositionIsPending || putAddonIsPending;

  return (
    <div
      key={activeComposition}
      className={twMerge(
        isRevalidating &&
          'pointer-events-none opacity-25 transition-opacity duration-200',
        'flex flex-col gap-y-48',
      )}
    >
      {compositionQuestions.isComposition && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            compositionSelectionForm.handleSubmit();
          }}
          className="flex gap-x-32"
        >
          {compositions.map((composition) => (
            <compositionSelectionForm.Field
              key={composition.guid}
              name="compositionGuid"
              listeners={{
                onChange: compositionSelectionForm.handleSubmit,
              }}
            >
              {(field) => {
                const isActive = activeComposition === composition.guid;
                return (
                  <CompositionCard
                    title={composition.product_name}
                    price={Number(composition.minimum_price)}
                    activities={composition.insured_activities.map(
                      (activity) => ({
                        title: activity.description,
                        icon: activity.icon,
                      }),
                    )}
                    onToggle={() => field.setValue(composition.guid)}
                    isActive={isActive}
                    moreInfo={
                      results.find((res) => res.data?.guid === composition.guid)
                        ?.data
                    }
                    billingUnit={customer.billingUnit}
                  />
                );
              }}
            </compositionSelectionForm.Field>
          ))}
        </form>
      )}
      {(!compositionQuestions.isComposition || activeComposition) && (
        <CompositionQuestions
          products={compositionQuestions}
          onSubmit={putAddonQuestion}
          billingUnit={customer.billingUnit}
        />
      )}
    </div>
  );
}

type CompositionQuestionsProps = {
  products: ProductsDto;
  onSubmit: (value: any) => void;
  billingUnit: BillingUnitDto;
};
function CompositionQuestions(props: CompositionQuestionsProps) {
  const { products, onSubmit, billingUnit } = props;

  const questions = useMemo(
    () => [
      ...(products.addOns?.map((addon) => ({
        ...addon,
        revalidateOnChange: true,
      })) || []),
      ...(products.addOns?.flatMap(
        (addon) =>
          addon.children?.map((child) => ({
            ...child,
            revalidateOnChange: true,
          })) || [],
      ) || []),
    ],
    [products],
  );

  const defaultValues = useMemo(
    () => calculateDefaultValues(questions),
    [questions],
  );

  const addonsForm = useForm({
    defaultValues,
    onSubmit: ({ value }) => onSubmit(mapToAnswer(value)),
  });

  useEffect(() => {
    addonsForm.reset();
  }, [defaultValues]);

  const productQuestions = useMemo(
    () =>
      products.questions.map((question) => ({
        ...question,
        revalidateOnChange: true,
      })),
    [products],
  );

  return (
    <div className="flex flex-col gap-y-48">
      {!!productQuestions.length && (
        <div>
          <Typography
            variant="body-l"
            weight="semibold"
            className="inline-flex mb-32"
          >
            Configureer het product
          </Typography>
          <Form
            className="flex flex-col gap-y-32"
            questions={productQuestions}
            onRevalidate={(values) => onSubmit(mapToAnswer(values))}
          />
        </div>
      )}
      <div>
        <Typography
          variant="body-l"
          weight="semibold"
          className="inline-flex mb-32"
        >
          Selecteer eventuele uitbreidingen
        </Typography>
        <form className="flex flex-col gap-y-32">
          {products.addOns?.map((addon) => (
            <addonsForm.Field
              key={addon.guid}
              name={addon.guid}
              listeners={{
                onChange: () => addonsForm.handleSubmit(),
              }}
            >
              {(field) => {
                const isActive = addon.answer.value === 'J';
                return (
                  <div
                    className={cardStyles({
                      variant: isActive ? 'outlined' : 'elevated',
                      className: twMerge(
                        'flex flex-col gap-y-20 p-20',
                        isActive && 'border-2 border-secondaryMain',
                      ),
                    })}
                  >
                    <div className="flex gap-x-12">
                      <FeaturedIcon
                        icon={mapper(addon.icon)}
                        variant="light-square"
                        size="xl"
                      />
                      <div className="flex flex-col gap-y-4 grow">
                        <Typography variant="display6" weight="semibold">
                          {addon.phrasing}
                        </Typography>
                        <Typography variant="body-s" weight="regular">
                          {addon.description}
                        </Typography>
                      </div>
                      <BigPricingPill
                        price={addon.price?.premiumAfterTax}
                        billingUnit={billingUnit}
                      />

                      <Switch
                        intent="on/off"
                        isSelected={isActive}
                        onChange={(value) =>
                          field.handleChange(
                            value
                              ? addon.options[0].value
                              : addon.options[1].value,
                          )
                        }
                      />
                    </div>
                    {field.state.value === addon.options[0].value &&
                      addon.children?.map((child) => (
                        <addonsForm.Field
                          key={child.guid}
                          name={child.guid}
                          listeners={{
                            onChange: addonsForm.handleSubmit,
                          }}
                        >
                          {(field) => (
                            <RadioList
                              variant="outlined"
                              items={child.options}
                              onChange={field.handleChange}
                              value={child.answer.value}
                            />
                          )}
                        </addonsForm.Field>
                      ))}
                  </div>
                );
              }}
            </addonsForm.Field>
          ))}
        </form>
      </div>
    </div>
  );
}
